import { AxiosError } from 'axios';

export const THROTTLE_ERROR_MESSAGE = 'Too many attempts. Please wait a moment and try again.';

export const LOCATION_REVOKED_MESSAGE =
  'The location you were using is no longer assigned to your account. Sign in again to continue.';

export const isThrottleError = (error: unknown): boolean => {
  return error instanceof AxiosError && error.response?.status === 429;
};

/**
 * A rejected `changeToken` (expired, already used, or otherwise invalid) answers with 401/403,
 * whereas a password-policy violation answers with 400/422. Distinguishing the two lets the
 * change-password form offer a recovery path (re-login) for the former and an inline, retryable
 * field error for the latter.
 */
export const isInvalidTokenError = (error: unknown): boolean => {
  if (!(error instanceof AxiosError)) {
    return false;
  }

  const status = error.response?.status;

  return status === 401 || status === 403;
};

/**
 * A bare `401 Unauthorized`. Used by the login wizard to detect a stale/expired `accountsProof` on the
 * second `check-credentials` or the final `login` — the gateway answers such a proof with `401` (not a
 * re-challenge), and the flow resets to the credential step since the proof cannot be renewed.
 */
export const isUnauthorizedError = (error: unknown): boolean =>
  error instanceof AxiosError && error.response?.status === 401;

const MFA_SESSION_EXPIRED_MESSAGE = 'MFA session expired or invalid';

/**
 * Distinguishes a dead `mfaPending` token from an in-ceremony error. Both come back as 401, so the
 * status alone is ambiguous — the auth-service tags each error with a `field`:
 *
 * - `field: 'token'`  → the pending token itself is dead (`MFA session expired or invalid`, or
 *   `Account not found` when the account was deactivated mid-ceremony). Unrecoverable in place
 *   (retrying resends the spent token), so route back to `/login` for a fresh challenge.
 * - `field: 'code'`   → retryable code error (`Invalid verification code`, `Verification code
 *   already used`, `Too many attempts — request a new code`, `MFA is not enabled for this account`,
 *   `No pending enrollment to confirm`) — keep the ceremony open and show it inline.
 * - `field: 'passkey'`→ passkey ceremony failure — retryable / pick another method, shown inline.
 *
 * So only a `field: 'token'` error (with the canonical message as a fallback) means the session is
 * gone. Everything else falls through to the inline `getApiErrorString` path.
 */
export const isMfaSessionExpiredError = (error: unknown): boolean => {
  if (!(error instanceof AxiosError)) {
    return false;
  }

  const messages = error.response?.data?.messages;
  if (!Array.isArray(messages)) {
    return false;
  }

  return messages.some(
    (message) =>
      message?.field === 'token' ||
      (typeof message?.message === 'string' && message.message.includes(MFA_SESSION_EXPIRED_MESSAGE)),
  );
};

export const getApiErrorString = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const messages = error.response?.data?.messages;
    if (Array.isArray(messages) && messages.length > 0) {
      return messages
        .map((x) => x?.message)
        .filter(Boolean)
        .join(', ');
    }
    return error.response?.data?.message || error.message || 'Unknown error';
  }

  if (error instanceof Error) {
    return error.message;
  }

  return String(error);
};

export const getLoginFlowError = (error: unknown): string => {
  if (isThrottleError(error)) {
    return THROTTLE_ERROR_MESSAGE;
  }

  return getApiErrorString(error);
};
